import { Box, Button, Stack } from "@mui/material";
import {
  type ApiProblemDetails,
  OrbitLTariff,
  ProductCode,
  TerminalFinancingModel,
} from "Infrastructure/Api/Api";
import { toast } from "sonner";
import { useAddMerchantAppProductMutation } from "Api/Mutations/Merchants/useAddMerchantAppProductMutation";
import { useGetMerchantAppProductsQuery } from "Api/Queries/Merchants/useGetMerchantAppProductsQuery";
import { isComgateProductCode } from "Components/Shared/Merchant/Products/AppProducts/constants";
import { Resources, type TFunction, useResource } from "Translations/Resources";
import { RadioButtons as RadioButtonsField } from "Components/Shared/Inputs/Form/RadioButtons";
import type { CodeListItem } from "Models/ICodeListDto";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useCreateMerchantFeeMutation } from "Api/Mutations/Merchants/useCreateMerchantFeeMutation";
import type { ProductPricingVariants } from "Components/Hunter/Merchants/types";
import { useProductPricingFeesList } from "Hooks/Options/useProductPricingFeesList";
import {
  productPricingSchema,
  type ProductPricingFormData,
} from "Components/Shared/Merchant/Products/AppProducts/ProductAccordion/AccordionContent/ProductActivationForm/schema";
import { TodoList } from "Components/Shared/TodoList/TodoList";
import type { PrerequisiteItem } from "Components/Shared/Merchant/Products/AppProducts/types";
import type { AppProductsVariant } from "Components/Shared/Merchant/Products/AppProducts/AppProducts";

const RESOURCES = Resources.Components.Merchant.AppProducts;
const ORBIT_L_RESOURCES = Resources.Components.Merchant.OrbitL;
const PRICING_RESOURCES = Resources.Components.Merchant.Pricings;

type AppProductsFormVariant = Exclude<AppProductsVariant, "readonly">;

// The terminal-count lock reports under the Orbit L key; the other 400s of the endpoint use
// Tariff, Region or TerminalCount.
const getAddErrorResource = (error: unknown) => {
  const apiErrors = (error as { data?: ApiProblemDetails })?.data?.errors;
  if (apiErrors && "OrbitL" in apiErrors) {
    return RESOURCES.Form.AddErrorCountsLocked;
  }
  return RESOURCES.Form.AddError;
};

const getOrbitLTariffConfig = (
  variant: AppProductsFormVariant,
  t: TFunction,
) => {
  const variantToOptions: Record<
    AppProductsFormVariant,
    Array<CodeListItem<OrbitLTariff>>
  > = {
    //hunter
    editable: [
      { code: OrbitLTariff.POS_1_CZ, name: t(ORBIT_L_RESOURCES.Tariff.Pos1Cz) },
      { code: OrbitLTariff.POS_2_CZ, name: t(ORBIT_L_RESOURCES.Tariff.Pos2Cz) },
      { code: OrbitLTariff.POS_3_CZ, name: t(ORBIT_L_RESOURCES.Tariff.Pos3Cz) },
      { code: OrbitLTariff.POS_4_CZ, name: t(ORBIT_L_RESOURCES.Tariff.Pos4Cz) },
    ],
    //selfServiceMerchant
    activatable: [
      { code: OrbitLTariff.POS_1_CZ, name: t(ORBIT_L_RESOURCES.Tariff.Pos1Cz) },
    ],
  };

  const options = variantToOptions[variant];

  return {
    options,
    defaultTariff: options[0].code,
  };
};

type Props = {
  code: ProductCode;
  defaultValues: Partial<ProductPricingFormData>;
  merchantPublicID: string;
  variant: AppProductsFormVariant;
  productPricings: Array<ProductPricingVariants>;
  prerequisites?: Array<PrerequisiteItem>;
};

export const ProductActivationForm = ({
  code,
  defaultValues,
  merchantPublicID,
  variant,
  productPricings,
  prerequisites = [],
}: Props) => {
  const { t } = useResource();
  const feesCodeList = useProductPricingFeesList(productPricings, code);
  const { mutateAsync: addProduct, isPending: isAddingProduct } =
    useAddMerchantAppProductMutation();
  const { mutate: createFees, isPending: isCreatingFees } =
    useCreateMerchantFeeMutation({
      onError: () => toast.error(t(PRICING_RESOURCES.PricingSettingError)),
    });

  // Orbit L and SoftPOS share one Comgate contract. The first of them sets the level (tariff), the second
  // only joins. Financing belongs to the physical terminals, so Orbit L asks for it whatever the order and
  // SoftPOS never does. Each device settles under its own price list, so no merchant fee is created here.
  const { data: merchantProducts } =
    useGetMerchantAppProductsQuery(merchantPublicID);
  const isComgateProduct = isComgateProductCode(code);
  const joinsExistingContract =
    isComgateProduct &&
    !!merchantProducts?.some(
      (p) =>
        p.productDto.code !== code && isComgateProductCode(p.productDto.code),
    );
  const createsContract = isComgateProduct && !joinsExistingContract;
  const isEditable = variant === "editable";
  const hasFinancingChoice = code === ProductCode.OrbitL && isEditable;
  const tariffConfig = createsContract
    ? getOrbitLTariffConfig(variant, t)
    : null;

  const { handleSubmit, control } = useForm<ProductPricingFormData>({
    resolver: zodResolver(productPricingSchema),
    defaultValues: {
      ...defaultValues,
      financingModel: TerminalFinancingModel.Legione,
      ...(tariffConfig ? { tariff: tariffConfig.defaultTariff } : {}),
    },
  });

  const onSubmit = async ({
    productPricingID,
    tariff,
    financingModel,
  }: ProductPricingFormData) => {
    try {
      await addProduct({
        merchantPublicID,
        productCode: code,
        ...(createsContract ? { tariff } : {}),
        ...(hasFinancingChoice ? { financingModel } : {}),
      });

      if (!isComgateProduct && productPricingID) {
        createFees({
          publicID: merchantPublicID,
          data: {
            productCode: code,
            merchantPricingID: Number(productPricingID),
            hunterLicenseDirectFee: 0,
          },
        });
      }
    } catch (error) {
      toast.error(t(getAddErrorResource(error)));
    }
  };

  const isFeeSetAutomatically = variant === "activatable";
  const hasManualPricing = !isComgateProduct && !isFeeSetAutomatically;
  const hasUnmetPrerequisites = prerequisites.some(
    ({ isCompleted }) => !isCompleted,
  );

  return (
    <Stack component="form" gap={1} onSubmit={handleSubmit(onSubmit)}>
      {prerequisites.length > 0 && (
        <TodoList
          title={t(RESOURCES.Form.Prerequisites.Title)}
          todoItems={prerequisites}
        />
      )}
      {hasFinancingChoice && (
        <RadioButtonsField
          control={control}
          name="financingModel"
          label={t(ORBIT_L_RESOURCES.Financing.Label)}
          codeList={[
            {
              code: TerminalFinancingModel.Legione,
              name: t(ORBIT_L_RESOURCES.Financing.Legione),
            },
            {
              code: TerminalFinancingModel.Hunter,
              name: t(ORBIT_L_RESOURCES.Financing.Hunter),
            },
          ]}
        />
      )}
      {tariffConfig && tariffConfig.options.length > 1 && (
        <Stack component="label">
          {t(ORBIT_L_RESOURCES.Tariff.Label)}
          <RadioButtonsField
            control={control}
            name="tariff"
            codeList={tariffConfig.options}
          />
        </Stack>
      )}
      {hasManualPricing && (
        <Stack component="label">
          {t(PRICING_RESOURCES.SelectPricingLabel)}
          <RadioButtonsField
            control={control}
            name="productPricingID"
            codeList={feesCodeList}
            onChangeValueTransform={(value) => Number(value)}
          />
        </Stack>
      )}
      <Box display="flex" justifyContent="center" alignItems="center">
        <Button
          variant="text"
          color="primary"
          size="medium"
          type="submit"
          disabled={hasUnmetPrerequisites}
          loading={isAddingProduct || isCreatingFees}
        >
          {t(RESOURCES.ProductAccordion.Activate)}
        </Button>
      </Box>
    </Stack>
  );
};
